import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

interface ComboSaleItem {
  comboId: string;
  comboName: string;
  comboType: string;
  quantity: number;
  totalRevenue: number;
  originalPrice: number;
  comboPrice: number;
  totalSavings: number;
  averageSavings: number;
}

interface DailyComboSales {
  date: string;
  quantity: number;
  revenue: number;
}

interface ComboSalesReport {
  topCombos: ComboSaleItem[];
  dailySales: DailyComboSales[];
  totalCombosSold: number;
  totalRevenue: number;
  totalSavings: number;
  averageSavingsPercent: number;
}

interface UseComboSalesReportParams {
  dateFrom: string;
  dateTo: string;
}

export function useComboSalesReport({ dateFrom, dateTo }: UseComboSalesReportParams) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["combo-sales-report", activeRestaurant?.id, dateFrom, dateTo],
    queryFn: async (): Promise<ComboSalesReport> => {
      if (!activeRestaurant) {
        return {
          topCombos: [],
          dailySales: [],
          totalCombosSold: 0,
          totalRevenue: 0,
          totalSavings: 0,
          averageSavingsPercent: 0,
        };
      }

      const startDate = new Date(dateFrom);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(dateTo);
      endDate.setHours(23, 59, 59, 999);

      // Fetch order items that have combo_id
      const { data: orderItems, error: itemsError } = await supabase
        .from("order_items")
        .select(`
          id,
          combo_id,
          quantity,
          unit_price,
          total_price,
          created_at,
          order:orders!inner(
            id,
            restaurant_id,
            status,
            created_at
          )
        `)
        .not("combo_id", "is", null)
        .eq("order.restaurant_id", activeRestaurant.id)
        .neq("order.status", "cancelled")
        .gte("order.created_at", startDate.toISOString())
        .lte("order.created_at", endDate.toISOString());

      if (itemsError) throw itemsError;

      // Fetch combos to get their details
      const { data: combos, error: combosError } = await supabase
        .from("combos")
        .select(`
          id,
          name,
          combo_type,
          price,
          combo_items(
            product_id,
            quantity,
            product:products(price)
          )
        `)
        .eq("restaurant_id", activeRestaurant.id);

      if (combosError) throw combosError;

      // Create combo map with calculated original prices
      const comboMap = new Map<string, {
        name: string;
        type: string;
        comboPrice: number;
        originalPrice: number;
      }>();

      combos?.forEach((combo) => {
        // Calculate original price (sum of individual product prices)
        let originalPrice = 0;
        combo.combo_items?.forEach((item: any) => {
          if (item.product?.price) {
            originalPrice += Number(item.product.price) * (item.quantity || 1);
          }
        });

        // If no items (pizza/rodizio), use combo price as original
        if (originalPrice === 0) {
          originalPrice = Number(combo.price);
        }

        comboMap.set(combo.id, {
          name: combo.name,
          type: combo.combo_type,
          comboPrice: Number(combo.price),
          originalPrice,
        });
      });

      // Aggregate sales by combo
      const comboSalesMap = new Map<string, {
        quantity: number;
        totalRevenue: number;
        totalOriginalPrice: number;
      }>();

      // Aggregate sales by date
      const dailySalesMap = new Map<string, { quantity: number; revenue: number }>();

      orderItems?.forEach((item: any) => {
        const comboId = item.combo_id;
        const quantity = item.quantity || 1;
        const revenue = Number(item.total_price) || 0;
        const comboInfo = comboMap.get(comboId);
        const originalPrice = comboInfo ? comboInfo.originalPrice * quantity : revenue;

        // Aggregate by combo
        const existing = comboSalesMap.get(comboId) || {
          quantity: 0,
          totalRevenue: 0,
          totalOriginalPrice: 0,
        };
        comboSalesMap.set(comboId, {
          quantity: existing.quantity + quantity,
          totalRevenue: existing.totalRevenue + revenue,
          totalOriginalPrice: existing.totalOriginalPrice + originalPrice,
        });

        // Aggregate by date
        const date = new Date(item.order.created_at).toISOString().split("T")[0];
        const dailyExisting = dailySalesMap.get(date) || { quantity: 0, revenue: 0 };
        dailySalesMap.set(date, {
          quantity: dailyExisting.quantity + quantity,
          revenue: dailyExisting.revenue + revenue,
        });
      });

      // Build top combos list
      const topCombos: ComboSaleItem[] = [];
      let totalCombosSold = 0;
      let totalRevenue = 0;
      let totalSavings = 0;

      comboSalesMap.forEach((sales, comboId) => {
        const comboInfo = comboMap.get(comboId);
        if (!comboInfo) return;

        const savings = sales.totalOriginalPrice - sales.totalRevenue;
        const averageSavings = sales.quantity > 0 ? savings / sales.quantity : 0;

        topCombos.push({
          comboId,
          comboName: comboInfo.name,
          comboType: comboInfo.type,
          quantity: sales.quantity,
          totalRevenue: sales.totalRevenue,
          originalPrice: comboInfo.originalPrice,
          comboPrice: comboInfo.comboPrice,
          totalSavings: savings,
          averageSavings,
        });

        totalCombosSold += sales.quantity;
        totalRevenue += sales.totalRevenue;
        totalSavings += savings;
      });

      // Sort by quantity (most sold first)
      topCombos.sort((a, b) => b.quantity - a.quantity);

      // Build daily sales array
      const dailySales: DailyComboSales[] = Array.from(dailySalesMap.entries())
        .map(([date, data]) => ({
          date,
          quantity: data.quantity,
          revenue: data.revenue,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Calculate average savings percent
      const totalOriginalPrice = totalRevenue + totalSavings;
      const averageSavingsPercent = totalOriginalPrice > 0 
        ? (totalSavings / totalOriginalPrice) * 100 
        : 0;

      return {
        topCombos,
        dailySales,
        totalCombosSold,
        totalRevenue,
        totalSavings,
        averageSavingsPercent,
      };
    },
    enabled: !!activeRestaurant,
  });
}
